// profesiones.js
//
// El catálogo de profesiones y la regla de "qué profesión tiene esta cuenta".
//
// Vivía dentro de VerificationWizard.js, que era donde se ELEGÍA. Desde el
// 09-sep-2026 el wizard ya no pregunta la profesión (Sebastián: "ya preguntamos
// una vez si es interno o médico, no nos interesan las otras de momento"), así
// que el catálogo dejó de pertenecerle: hoy solo se usa para MOSTRAR la
// profesión que una cuenta ya tiene (perfil, directorio, tarjeta de colega).
//
// La lista se conserva COMPLETA a propósito. Hay cuentas guardadas con
// KINESIOLOGIST / NURSE / OTHER —el wizard las ofreció durante meses— y sin su
// entrada acá el perfil de esa gente quedaría sin etiqueta ni ícono. Que no se
// pregunten más no significa que no existan.

export const PROFESSIONS = [
  { value: "DOCTOR", label: "Médico", icon: "🩺", desc: "Médico cirujano" },
  {
    value: "INTERNO",
    label: "Interno/a",
    icon: "🎓",
    desc: "Interno/a de medicina",
  },
  {
    value: "ESTUDIANTE",
    label: "Estudiante de medicina",
    icon: "📚",
    desc: "Estudiante de medicina",
  },
  {
    value: "KINESIOLOGIST",
    label: "Kinesiólogo/a",
    icon: "🤸",
    desc: "Profesional de kinesiología",
  },
  {
    value: "NURSE",
    label: "Enfermero/a",
    icon: "💉",
    desc: "Profesional de enfermería",
  },
  {
    value: "TENS",
    label: "TENS",
    icon: "🩹",
    desc: "Técnico en enfermería de nivel superior",
  },
  {
    value: "OTHER",
    label: "Otro profesional",
    icon: "🧑‍⚕️",
    desc: "Otro profesional de la salud",
  },
  {
    value: "NO_SALUD",
    label: "No es de la salud",
    icon: "👤",
    desc: "Persona que no es del área de la salud",
  },
];

// Default cuando la cuenta no dijo NADA (ni profesión guardada, ni etapa en la
// bienvenida). Es DOCTOR y no INTERNO a propósito: el titulado es el camino
// EXIGENTE del wizard (N° de registro + certificado de la Superintendencia),
// mientras que al interno se le pide solo el carnet. Errar hacia INTERNO sería
// regalarle el acceso provisional a cualquiera con menos papeles de los que le
// corresponden —y ese mismo request enciende el Modo Turno (#878)—; errar hacia
// DOCTOR, en el peor caso, le pide un papel de más a alguien que después lo
// corrige. Coincide además con el resto del modelo, donde `cargo` vacío se
// asume "Dr." al firmar.
export const PROFESION_POR_DEFECTO = "DOCTOR";

/**
 * Qué profesión le corresponde a esta cuenta, sin volver a preguntársela.
 *
 * Orden de prioridad, y el orden ES la regla:
 *   1. `profile.profession` — ya la eligió alguna vez (o un admin se la puso).
 *      Nunca se pisa: es la única forma de que las cuentas viejas con
 *      KINESIOLOGIST / NURSE / OTHER sigan siendo lo que son.
 *   2. La etapa que respondió en la bienvenida: "interno" → INTERNO. Es
 *      literalmente la pregunta "¿interno o médico?" que ya se hizo una vez;
 *      volver a hacerla en el wizard era el paso que Sebastián mandó sacar.
 *      `etapaReciente` cubre el instante en que la respuesta ya se eligió pero
 *      todavía no volvió en /api/user/me/ (se navega al wizard sin esperar el
 *      PATCH, para no frenar a nadie con el wifi del hospital).
 *   3. El default de arriba.
 *
 * "becado" y "staff" son médicos titulados; "otro" cae al default por la misma
 * razón que el caso vacío.
 */
export const profesionDeCuenta = (profile, etapaReciente) => {
  if (profile?.profession) return profile.profession;
  const etapa = etapaReciente || profile?.etapa || "";
  if (etapa === "interno") return "INTERNO";
  // #1738 R10: el estudiante se trata como el interno en papeles y Términos, pero conserva su profesión.
  if (etapa === "estudiante") return "ESTUDIANTE";
  return PROFESION_POR_DEFECTO;
};

// "En formación" = no titulado: no tiene N° de registro ni certificado de la Superintendencia, así que
// solo se le pide el carnet. #1738 R10: el estudiante cuenta igual que el interno. El nombre se
// conserva (`esInterno`) porque lo importan VerificationWizard y sus tests.
export const esInterno = (profesion) => profesion === "INTERNO" || profesion === "ESTUDIANTE";
